/**
 * Phase 13b — "Used by Autopilot" section for the Strategy Memory drawer.
 *
 * Fetches `GET /autopilot/actions?evidence_entry_id={id}&limit=5` and
 * renders a compact list of the recent autopilot actions that cite this
 * memory entry. Section is entirely hidden when the endpoint returns
 * zero rows so the drawer stays clean for entries no autopilot has
 * touched yet.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Bot } from "lucide-react";

const COPY = {
  de: {
    header: "Autopilot-Aktionen mit diesem Eintrag",
    outcome: "Ergebnis",
    open: "Öffnen",
  },
  en: {
    header: "Autopilot recommendations using this entry",
    outcome: "Outcome",
    open: "Open",
  },
};

const STATE_TONE = {
  executed: "border-emerald-300 bg-emerald-50 text-emerald-800",
  approved: "border-emerald-300 bg-emerald-50 text-emerald-800",
  pending: "border-amber-300 bg-amber-50 text-amber-800",
  queued_for_approval: "border-amber-300 bg-amber-50 text-amber-800",
  rejected: "border-rose-300 bg-rose-50 text-rose-800",
  failed: "border-rose-300 bg-rose-50 text-rose-800",
};

export default function AutopilotUsageSection({ entryId, locale = "de" }) {
  const t = COPY[locale] || COPY.en;
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/autopilot/actions?evidence_entry_id=${entryId}&limit=5`)
      .then((r) => { if (alive) setRows(r.data?.items || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [entryId]);

  if (!rows || rows.length === 0) return null;
  return (
    <div data-testid="sm-drawer-autopilot-usage">
      <div className="text-xs uppercase text-muted-foreground mb-2 inline-flex items-center gap-1">
        <Bot className="size-3" />
        {t.header}
      </div>
      <ul
        className="space-y-1.5"
        data-testid="sm-drawer-autopilot-usage-list"
      >
        {rows.map((a) => (
          <li
            key={a.id}
            data-testid={`sm-drawer-autopilot-usage-row-${a.id}`}
            className="flex items-center gap-2 rounded-md border p-2 text-xs"
          >
            <Badge
              variant="outline"
              className={"font-mono text-[10px] " +
                (STATE_TONE[a.state] || "text-muted-foreground")}
              data-testid={`sm-drawer-autopilot-usage-state-${a.id}`}
            >
              {a.state}
            </Badge>
            <div className="flex-1 min-w-0">
              <div className="truncate font-medium">
                {a.action_type || "—"}
              </div>
              <div className="text-muted-foreground truncate">
                {a.target_resource_type}
                {a.target_resource_id
                  ? `/${String(a.target_resource_id).slice(0, 8)}` : ""}
                {a.outcome_improvement_pct != null && (
                  <span className={
                      "ml-2 tabular-nums " +
                      (a.outcome_improvement_pct > 0
                        ? "text-emerald-700" : "text-rose-700")
                  }>
                    {t.outcome}: {a.outcome_improvement_pct > 0 ? "+" : ""}
                    {a.outcome_improvement_pct.toFixed(1)}%
                    {a.outcome_metric_type
                      ? ` ${a.outcome_metric_type}` : ""}
                  </span>
                )}
              </div>
            </div>
            <Link
              to={`/app/autopilot/actions?state=${a.state}`}
              className="text-primary hover:underline"
              data-testid={`sm-drawer-autopilot-usage-open-${a.id}`}
            >{t.open}</Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
